import { useMemo } from "react";
import { Timer, AlertTriangle, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { buildSlides, spokenSeconds, formatClock, WORDS_PER_MINUTE, DEFAULT_FONT_PT } from "@/lib/slides";

export function RehearsalPanel({ open, onClose, session, result, onStartWalkthrough }) {
  const fontSize = session?.slide_font_size || DEFAULT_FONT_PT;
  const segments = session?.segments || [];

  const rows = useMemo(() => {
    const slides = buildSlides(segments, fontSize);
    const actualBySegment = {};
    if (result) {
      for (const t of result.timings) {
        const slide = slides[t.slideIndex];
        if (!slide) continue;
        actualBySegment[slide.segmentIndex] = (actualBySegment[slide.segmentIndex] || 0) + t.seconds;
      }
    }
    return segments.map((seg, i) => {
      const planned = (seg.duration_minutes || 5) * 60;
      const estimated = spokenSeconds(seg.body);
      return {
        title: seg.title || "Untitled segment",
        category: seg.category,
        slides: slides.filter((s) => s.segmentIndex === i).length,
        planned,
        estimated,
        actual: actualBySegment[i],
        over: estimated > planned,
      };
    });
  }, [segments, fontSize, result]);

  const totals = rows.reduce(
    (acc, r) => ({
      planned: acc.planned + r.planned,
      estimated: acc.estimated + r.estimated,
      actual: acc.actual + (r.actual || 0),
      slides: acc.slides + r.slides,
    }),
    { planned: 0, estimated: 0, actual: 0, slides: 0 }
  );
  const overruns = rows.filter((r) => r.over);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="rehearsal-panel">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Session rehearsal</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Slides" value={totals.slides} testId="rehearsal-total-slides" />
          <Stat label="Planned" value={formatClock(totals.planned)} testId="rehearsal-total-planned" />
          <Stat
            label={result ? "You took" : "Est. spoken"}
            value={formatClock(result ? totals.actual : totals.estimated)}
            testId="rehearsal-total-estimated"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Estimated at a prayer pace of {WORDS_PER_MINUTE} words per minute. Silence, music and pauses are not counted.
        </p>

        {overruns.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-[var(--brand-gold)] bg-amber-50 dark:bg-amber-950/30 px-3 py-2" data-testid="rehearsal-overrun-warning">
            <AlertTriangle className="h-4 w-4 text-[var(--brand-gold-text)] dark:text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs">
              {overruns.length} segment{overruns.length > 1 ? "s" : ""} read longer than the time allowed:{" "}
              <span className="font-medium">{overruns.map((r) => r.title).join(", ")}</span>
            </p>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto divide-y divide-[var(--brand-border)] dark:divide-[var(--brand-border-dark)]" data-testid="rehearsal-segment-list">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-3">Add segments to rehearse this session.</p>}
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2 gap-3" data-testid={`rehearsal-row-${i}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {r.category} · {r.slides} {r.slides === 1 ? "slide" : "slides"}
                </p>
              </div>
              <div className="flex items-center gap-4 font-mono text-xs shrink-0">
                <span className="text-muted-foreground">{formatClock(r.planned)} planned</span>
                <span className={r.over ? "text-[var(--brand-crimson)] font-semibold" : "text-foreground"}>
                  {formatClock(r.actual != null ? r.actual : r.estimated)} {r.actual != null ? "actual" : "est."}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="rehearsal-close-button">
            Close
          </Button>
          <Button onClick={onStartWalkthrough} disabled={rows.length === 0} data-testid="rehearsal-start-walkthrough-button">
            {result ? <Timer className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {result ? "Rehearse again" : "Start timed walk-through"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-md border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] px-3 py-2">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-lg font-display font-semibold" data-testid={testId}>{value}</p>
    </div>
  );
}
